import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Table, Tag, Radio, Typography, Button, Checkbox, message, Modal, Tooltip } from "antd";
import { useReactToPrint } from "react-to-print";
import { isValid, isValidArray, checkActionAllowed } from "../../../utils/utilities";
import { roundNumber } from "../../../utils/math.utils";
import { getLocalDateAndTime } from "../../../utils/date.utils";
import useColumnSearch from "../../../ui-components/table-column-search";
import { NotifyButton, NotifyComponent } from "../../ticket-summary-components/notify.component";
import { RefundButton } from "../../ticket-summary-components/refund-btn";
import { ExceptionalRefundModal } from "../../ticket-summary-components/refund-at-trip-level";
import { showConfirm } from "../../custom-components/custom-component";
import { TicketSummary } from "../../ticket-summary-components/ticket-summary.component";
import { SeatClickTicketPopUp } from "../../ticket-summary-components/seat-click-ticket-popup.component";
import { sendTicketUpdateMailById, sendTicketUpdateSMSById, cancelTicket, getTicketById } from "../../../actions/ticket.action";
import { ACTION_EXCEPTIONAL_REFUND_ALLOWED, ACTION_TICKET_NOTIFY_BTN } from "../../../utils/constant";
import { groupTicketsByPnr, exceptionalRefund } from "./trip-tickets.module";

const { Text, Title } = Typography;

const VIEW_MODE_PNR = "pnr";
const VIEW_MODE_PASSENGER = "passenger";

const renderStatusTag = (status) => {
    let color = "green";
    if (status === "Blocked") {
        color = "orange";
    } else if (status === "Mixed") {
        color = "blue";
    } else if (status === "Cancelled") {
        color = "red";
    }
    return <Tag color={color}>{status}</Tag>;
};

const renderSeats = (seatNumbers) => {
    if (!isValidArray(seatNumbers) || seatNumbers.length === 0) {
        return null;
    }
    if (seatNumbers.length <= 3) {
        return seatNumbers.join(", ");
    }
    const visible = seatNumbers.slice(0, 3).join(", ");
    const more = seatNumbers.length - 3;
    return (
        <Tooltip title={seatNumbers.join(", ")}>
            <span>{visible} <Text type="secondary">+{more}</Text></span>
        </Tooltip>
    );
};

const renderTruncated = (text, maxChars = 12) => {
    if (!text) return "-";
    if (text.length <= maxChars) return text;
    const truncated = text.slice(0, maxChars).trimEnd();
    return (
        <Tooltip title={text}>
            <span>{truncated}…</span>
        </Tooltip>
    );
};

const DetailRow = ({ label, value }) => (
    <div className="journey-detail-row">
        <span className="journey-label">{label}</span>
        <span className="journey-value">{value}</span>
    </div>
);

const JourneyDetails = ({ row, selectedSchedule }) => {
    if (!isValidArray(selectedSchedule)) {
        return null;
    }
    return (
        <div className="active-journey-details">
            <Row gutter={[24, 8]}>
                <Col xs={24} md={12}>
                    <Title level={5} className="section-title">JOURNEY</Title>
                    <DetailRow label="PNR" value={row.pnr || row.actual_pnr || row.pnrKey || "-"} />
                    <DetailRow label="Status" value={renderStatusTag(row.traveller_status)} />
                    <DetailRow label="Pickup" value={row.location || "-"} />
                    <DetailRow label="Drop" value={row.drop_location || "-"} />
                    {selectedSchedule.description &&
                        <DetailRow label="Bus" value={selectedSchedule.description} />
                    }
                </Col>
                <Col xs={24} md={12}>
                    <Title level={5} className="section-title">PAYMENT</Title>
                    <DetailRow label="Channel" value={row.agent_name || "-"} />
                    <DetailRow label="Total fare" value={isValid(row.totalFare) ? `Rs.${roundNumber(row.totalFare)}` : (isValid(row.amount_paid) ? `Rs.${roundNumber(row.amount_paid)}` : "-")} />
                    <DetailRow label="Booked" value={row.booking_time ? getLocalDateAndTime(row.booking_time) : "-"} />
                </Col>
            </Row>
        </div>
    );
};

const ResendConfirmationButton = ({ ticketId }) => {
    const [loading, setLoading] = useState(false);
    return (
        <Button
            className="outline-button"
            loading={loading}
            onClick={() => {
                showConfirm(() => {
                    setLoading(true);
                    let pending = 2;
                    const done = () => {
                        pending = pending - 1;
                        if (pending <= 0) {
                            setLoading(false);
                        }
                    };
                    sendTicketUpdateMailById(ticketId, () => {
                        message.success("Confirmation email sent");
                        done();
                    }, (msg) => {
                        message.error(msg || "Failed to send email");
                        done();
                    });
                    sendTicketUpdateSMSById(ticketId, () => {
                        message.success("Confirmation SMS sent");
                        done();
                    }, (msg) => {
                        message.error(msg || "Failed to send SMS");
                        done();
                    });
                }, "Confirmation", "Resend confirmation email and SMS to the passenger?", "Yes");
            }}
        >
            Resend confirmation
        </Button>
    );
};

const PrintTicketButton = ({ ticketId }) => {
    const [loading, setLoading] = useState(false);
    const [ticketDetails, setTicketDetails] = useState(null);
    const contentRef = useRef();
    const handlePrint = useReactToPrint({ contentRef });

    const fetchAndPrint = () => {
        setLoading(true);
        getTicketById(ticketId, (data) => {
            setTicketDetails(data);
            setLoading(false);
            setTimeout(() => handlePrint(), 100);
        }, () => {
            setLoading(false);
            message.error("Failed to load ticket for printing");
        });
    };

    return (
        <span className="print-ticket-action">
            <Button className="outline-button" loading={loading} onClick={fetchAndPrint}>Print ticket</Button>
            <div className="printer-render">
                <div ref={contentRef} className="printer-render">
                    {ticketDetails &&
                        <TicketSummary
                            ticketDetails={ticketDetails}
                            isPrinter={true}
                        />
                    }
                </div>
            </div>
        </span>
    );
};

const NotBoardedSelector = ({ row, onConfirm }) => {
    const passengers = isValidArray(row.seats) ? row.seats : [];
    const [selected, setSelected] = useState({});
    const toggle = (id) => {
        setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
    };
    const selectedTravellers = passengers.filter((p) => selected[p.travellerId]);
    return (
        <div>
            <Row><Col><Text>Select passengers to mark as not boarded:</Text></Col></Row>
            {passengers.map((p) => (
                <Row key={p.travellerId} align="middle" gutter={[8, 4]}>
                    <Col>
                        <Checkbox
                            checked={!!selected[p.travellerId]}
                            onChange={() => toggle(p.travellerId)}
                        />
                    </Col>
                    <Col>{p.seat_number}</Col>
                    <Col>{p.full_name}</Col>
                </Row>
            ))}
            <Row className="marginTop">
                <Col>
                    <Button
                        disabled={selectedTravellers.length === 0}
                        onClick={() => onConfirm(selectedTravellers)}
                    >Mark Not Boarded</Button>
                </Col>
            </Row>
        </div>
    );
};

const ActionButtons = ({ row, reload, onCancelClick, selectedSchedule }) => {
    const [notBoardedVisible, setNotBoardedVisible] = useState(false);
    const passengerSeats = isValidArray(row.seats) ? row.seats : [row];
    const isCancelAllowed = checkActionAllowed('CANCEL_TICKET_ALLOWED');

    const performNotBoarded = (selectedTravellers) => {
        const first = passengerSeats[0];
        const ticketObj = {
            id: first.ticket_id,
            trip_id: first.trip_id,
            trip_route_id: first.trip_route_id
        };
        const formattedTravellers = selectedTravellers.map((t) => ({
            id: t.travellerId,
            fullName: t.full_name,
            seatNumber: t.seat_number,
            pickup_location: t.pickup_location || { id: t.pickup_location_id }
        }));
        const partialCancel = selectedTravellers.length !== passengerSeats.length;
        cancelTicket(ticketObj, formattedTravellers, partialCancel, false, 0, false, () => {
            message.success("Marked as not boarded");
            setNotBoardedVisible(false);
            reload();
        });
    };

    return (
        <Row gutter={[8, 8]} className="active-action-buttons">
            <Col>
                <SeatClickTicketPopUp
                    ticketId={row.ticket_id}
                    agentId={row.agent_id}
                    isButtonClick={true}
                    buttonText="View ticket"
                    buttonClassName="outline-button"
                    children={null}
                />
            </Col>
            <Col>
                <SeatClickTicketPopUp
                    ticketId={row.ticket_id}
                    agentId={row.agent_id}
                    isButtonClick={true}
                    buttonText="Edit details"
                    buttonClassName="outline-button"
                    children={null}
                />
            </Col>
            <Col>
                <ResendConfirmationButton ticketId={row.ticket_id} />
            </Col>
            <Col>
                <PrintTicketButton ticketId={row.ticket_id} />
            </Col>
            <Col>
                <Button className="outline-button" onClick={() => setNotBoardedVisible(true)}>Mark as not boarded</Button>
                <Modal
                    title="Mark as not boarded"
                    visible={notBoardedVisible}
                    onCancel={() => setNotBoardedVisible(false)}
                    footer={null}
                    destroyOnClose
                >
                    <NotBoardedSelector row={row} onConfirm={performNotBoarded} />
                </Modal>
            </Col>
            {isCancelAllowed &&
                <Col>
                    <Button className="outline-button" onClick={() => onCancelClick(row)}>Cancel ticket</Button>
                </Col>
            }
        </Row>
    );
};

const ExpandedRowContent = ({ row, viewMode, selectedSchedule, reload, onCancelClick }) => {
    const passengers = isValidArray(row.seats) ? row.seats : [];
    return (
        <div className="active-expanded-row">
            {viewMode === VIEW_MODE_PNR && passengers.length > 0 &&
                <div className="passengers-section">
                    <Title level={5} className="section-title">PASSENGERS ({passengers.length})</Title>
                    <Table
                        rowKey="travellerId"
                        size="small"
                        pagination={false}
                        showHeader={false}
                        dataSource={passengers}
                        columns={[
                            { title: 'Seat', dataIndex: 'seat_number', key: 'seat_number', width: 60 },
                            {
                                title: 'Name',
                                dataIndex: 'full_name',
                                key: 'full_name',
                                render: (text, record) => (
                                    <span>
                                        <Text strong>{text}</Text>
                                        {record.gender ? <Text type="secondary"> ({record.gender}{record.age ? `, ${record.age}` : ''})</Text> : null}
                                    </span>
                                )
                            },
                            {
                                title: 'Status',
                                dataIndex: 'traveller_status',
                                key: 'traveller_status',
                                render: (text) => renderStatusTag(text)
                            }
                        ]}
                    />
                </div>
            }
            <JourneyDetails row={row} selectedSchedule={selectedSchedule} />
            <ActionButtons
                row={row}
                reload={reload}
                onCancelClick={onCancelClick}
                selectedSchedule={selectedSchedule}
            />
        </div>
    );
};

export const ActivePassengersTab = ({
    confirmTickets,
    blockedTickets,
    selectedSchedule,
    tripId,
    reload,
    onCancelClick
}) => {
    const [viewMode, setViewMode] = useState(VIEW_MODE_PNR);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedPNRs, setSelectedPNRs] = useState([]);
    const [selectedData, setSelectedData] = useState([]);
    const [notifyModalVisible, setNotifyModalVisible] = useState(false);
    const [exceptionalRefundModalVisible, setExceptionalRefundModalVisible] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    const allActiveTickets = useMemo(() => {
        const confirmed = isValidArray(confirmTickets) ? confirmTickets : [];
        const blocked = isValidArray(blockedTickets) ? blockedTickets : [];
        return [...confirmed, ...blocked];
    }, [confirmTickets, blockedTickets]);

    const groupedTickets = useMemo(() => groupTicketsByPnr(allActiveTickets), [allActiveTickets]);

    const filterOptions = useMemo(() => {
        const boardings = new Set();
        const drops = new Set();
        const agents = new Set();
        const statuses = new Set();
        const source = isValidArray(allActiveTickets) ? allActiveTickets : [];
        source.forEach((t) => {
            if (t.location) boardings.add(t.location);
            if (t.drop_location) drops.add(t.drop_location);
            if (t.agent_name) agents.add(t.agent_name);
            if (t.traveller_status) statuses.add(t.traveller_status);
        });
        const toFilter = (set) => Array.from(set).map((v) => ({ text: v, value: v }));
        return {
            boardings: toFilter(boardings),
            drops: toFilter(drops),
            agents: toFilter(agents),
            statuses: toFilter(statuses)
        };
    }, [allActiveTickets]);

    const dataSource = viewMode === VIEW_MODE_PNR ? groupedTickets : allActiveTickets;
    const rowKey = viewMode === VIEW_MODE_PNR ? 'pnrKey' : 'travellerId';

    const clearSelection = () => {
        setSelectedRowKeys([]);
        setSelectedPNRs([]);
        setSelectedData([]);
    };

    // Derive the PNR list and flattened seat list from the selected table rows.
    const applySelection = (keys, rows) => {
        setSelectedRowKeys(keys);
        const pnrs = [];
        const flattenedSeats = [];
        rows.forEach((r) => {
            const pnr = r.actual_pnr || r.pnr || r.pnrKey;
            if (pnr && pnrs.indexOf(pnr) === -1) {
                pnrs.push(pnr);
            }
            if (isValidArray(r.seats)) {
                r.seats.forEach((s) => flattenedSeats.push(s));
            } else {
                flattenedSeats.push(r);
            }
        });
        setSelectedPNRs(pnrs);
        setSelectedData(flattenedSeats);
    };

    const onExceptionalRefundSuccess = () => {
        message.success("Exceptional refund applied successfully");
        setExceptionalRefundModalVisible(false);
        clearSelection();
        reload();
    };
    const onExceptionalRefundFailure = (msg) => message.error(msg);

    // Fresh trip data (reload / trip change) invalidates whatever was selected before.
    useEffect(() => {
        clearSelection();
    }, [confirmTickets, blockedTickets]);

    const isExceptionalRefundAllowed = isValid(global.allowedActions[ACTION_EXCEPTIONAL_REFUND_ALLOWED]) ? true : false;

    const rowSelection = {
        selectedRowKeys,
        onChange: applySelection
    };

    // Antd keeps rows that are hidden by a column filter selected. Drop them so the
    // selection (and everything derived from it: counts, Notify, Refund) only ever
    // contains rows the user can see.
    const onTableChange = (pagination, tableFilters, sorter, extra) => {
        if (!isValidArray(selectedRowKeys)) {
            return;
        }
        const visible = isValidArray(extra && extra.currentDataSource) ? extra.currentDataSource : [];
        const visibleRows = visible.filter((row) => selectedRowKeys.includes(row[rowKey]));
        if (visibleRows.length !== selectedRowKeys.length) {
            applySelection(visibleRows.map((row) => row[rowKey]), visibleRows);
        }
    };

    const hasBlockedSelected = selectedData.some((r) => r.traveller_status === 'Blocked');
    const refundDisabled = selectedData.length === 0 || hasBlockedSelected;

    const seatColumn = {
        title: 'Seat',
        dataIndex: 'seat_number',
        key: 'seat_number',
        width: 85,
        ellipsis: true,
        render: (text, row) => {
            if (viewMode === VIEW_MODE_PNR && isValidArray(row.seatNumbers)) {
                return renderSeats(row.seatNumbers);
            }
            return text;
        }
    };

    const passengerColumn = {
        title: 'Passenger',
        dataIndex: 'full_name',
        key: 'full_name',
        width: 130,
        ellipsis: true,
        ...useColumnSearch({ dataIndexExt: 'full_name' }),
        render: (text, row) => {
            if (viewMode === VIEW_MODE_PNR && row.seatCount > 1) {
                const extra = <Text type="secondary"> +{row.seatCount - 1}</Text>;
                if (!text || text.length <= 14) {
                    return <span>{text}{extra}</span>;
                }
                return (
                    <Tooltip title={text}>
                        <span>{text.slice(0, 14).trimEnd()}…{extra}</span>
                    </Tooltip>
                );
            }
            return renderTruncated(text, 16);
        }
    };

    const columns = [
        seatColumn,
        passengerColumn,
        {
            title: 'Phone',
            dataIndex: 'phone_number',
            key: 'phone_number',
            width: 105,
            ellipsis: true,
            ...useColumnSearch({ dataIndexExt: 'phone_number' })
        },
        {
            title: 'Boarding',
            dataIndex: 'location',
            key: 'location',
            width: 95,
            ellipsis: true,
            filters: filterOptions.boardings,
            filterSearch: true,
            onFilter: (value, record) => record.location === value,
            render: (text) => renderTruncated(text, 8)
        },
        {
            title: 'Drop',
            dataIndex: 'drop_location',
            key: 'drop_location',
            width: 95,
            ellipsis: true,
            filters: filterOptions.drops,
            filterSearch: true,
            onFilter: (value, record) => record.drop_location === value,
            render: (text) => renderTruncated(text, 8)
        },
        {
            title: 'Agent',
            dataIndex: 'agent_name',
            key: 'agent_name',
            width: 85,
            ellipsis: true,
            filters: filterOptions.agents,
            filterSearch: true,
            onFilter: (value, record) => record.agent_name === value,
            render: (text) => renderTruncated(text, 8)
        },
        {
            title: 'Fare',
            key: 'fare',
            width: 80,
            render: (text, row) => {
                if (viewMode === VIEW_MODE_PNR) {
                    return isValid(row.totalFare) ? `Rs.${roundNumber(row.totalFare)}` : '-';
                }
                return isValid(row.amount_paid) ? `Rs.${roundNumber(row.amount_paid)}` : (row.fare || '-');
            }
        },
        {
            title: 'Booked',
            dataIndex: 'booking_time',
            key: 'booking_time',
            width: 145,
            render: (text) => text ? <span className="nowrap-cell">{getLocalDateAndTime(text)}</span> : '-',
            sorter: (a, b) => {
                const av = a.booking_time ? new Date(a.booking_time).getTime() : 0;
                const bv = b.booking_time ? new Date(b.booking_time).getTime() : 0;
                return av - bv;
            }
        },
        {
            title: 'Status',
            dataIndex: 'traveller_status',
            key: 'traveller_status',
            width: 105,
            filters: filterOptions.statuses,
            onFilter: (value, record) => record.traveller_status === value,
            render: (text) => renderStatusTag(text)
        }
    ];

    return (
        <div className="active-passengers-tab">
            <Row className="active-toolbar" justify="space-between" align="middle" gutter={[8, 8]}>
                <Col>
                    <Row gutter={[8, 8]} align="middle" className="toolbar-buttons">
                        {checkActionAllowed(ACTION_TICKET_NOTIFY_BTN) &&
                            <Col>
                                <NotifyButton
                                    onClick={() => setNotifyModalVisible(true)}
                                    showDownArrow={false}
                                    disabled={selectedPNRs.length === 0}
                                    label="Notify selected"
                                />
                            </Col>
                        }
                        {isExceptionalRefundAllowed &&
                            <Col>
                                <Tooltip title={hasBlockedSelected ? "Refund is allowed only for confirmed seats" : ""}>
                                    <RefundButton
                                        onClick={() => setExceptionalRefundModalVisible(true)}
                                        disabled={refundDisabled}
                                    >Refund</RefundButton>
                                </Tooltip>
                            </Col>
                        }
                        {selectedPNRs.length > 0 &&
                            <Col><Text type="secondary">{selectedPNRs.length} PNR(s) selected</Text></Col>
                        }
                    </Row>
                </Col>
                <Col>
                    <Radio.Group
                        value={viewMode}
                        onChange={(e) => {
                            setViewMode(e.target.value);
                            clearSelection();
                        }}
                    >
                        <Radio.Button value={VIEW_MODE_PNR}>Group by PNR</Radio.Button>
                        <Radio.Button value={VIEW_MODE_PASSENGER}>All passengers</Radio.Button>
                    </Radio.Group>
                </Col>
            </Row>
            <Row className="active-summary">
                <Col>
                    <Text strong>{groupedTickets.length} bookings</Text>
                    <Text type="secondary"> · {allActiveTickets.length} seats</Text>
                </Col>
            </Row>
            <Table
                className="app-table active-passengers-table clickable-rows-table"
                rowKey={rowKey}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                onChange={onTableChange}
                expandable={{
                    expandedRowKeys,
                    onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
                    expandedRowRender: (row) => (
                        <ExpandedRowContent
                            row={row}
                            viewMode={viewMode}
                            selectedSchedule={selectedSchedule}
                            reload={reload}
                            onCancelClick={onCancelClick}
                        />
                    ),
                    rowExpandable: () => true,
                    expandIcon: () => null,
                    columnWidth: 0
                }}
                onRow={(row) => ({
                    onClick: (e) => {
                        if (e.target.closest('button') || e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.ant-checkbox')) {
                            return;
                        }
                        const key = row[rowKey];
                        setExpandedRowKeys((prev) => {
                            if (prev.includes(key)) {
                                return prev.filter((k) => k !== key);
                            }
                            return [...prev, key];
                        });
                    }
                })}
            />
            <Modal
                title="Notify"
                visible={notifyModalVisible}
                onCancel={() => setNotifyModalVisible(false)}
                footer={null}
                destroyOnClose
            >
                {selectedPNRs.length > 0 &&
                    <NotifyComponent
                        pnrs={selectedPNRs}
                        travelDate={selectedSchedule && selectedSchedule.start_date}
                        onCancel={() => setNotifyModalVisible(false)}
                        notifyModalVisible={notifyModalVisible}
                        tripId={tripId}
                        sentLocation={`${global.userName}(Passenger tab)`}
                        showPnrs={true}
                    />
                }
            </Modal>
            <ExceptionalRefundModal
                visible={exceptionalRefundModalVisible}
                tripId={tripId}
                onCancel={() => setExceptionalRefundModalVisible(false)}
                onApply={(data) => {
                    exceptionalRefund(data, tripId, onExceptionalRefundSuccess, onExceptionalRefundFailure);
                }}
                ticketData={selectedData}
                title="Exceptional Refund"
                subtitle={selectedSchedule && selectedSchedule.description}
                isExceptionalRefundAllowed={isExceptionalRefundAllowed}
            />
        </div>
    );
};
